import { FC, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Highlighter from 'react-highlight-words';
import Utils from '../utils';
import ProductCover from '../common/ProductCover';
import SelectButton from '../common/SelectButton';
import { PreviewButton } from '../preview/PreviewButton';
import ProductStatistics from '../product/ProductStatistics';
import ProductAge from '../product/ProductAge';
import { ProductFormat } from '../product/ProductFormat';
import { ProductReleaseDate } from '../product/ProductReleaseDate';
import ProductUsageConditions from '../product/ProductUsageConditions';
import ProductLanguage from '../product/ProductLanguage';
import RoutingUtils from '../utils/utils-routing';
import { concreteLoanFormatNameToKey } from '../../store/loanFormat/actions';
import { ListBadges } from '../common/BadgesV2';
import { CenteredListBadges } from '../common/BadgesV2/CenteredBadgeContainer';
import MetadataUtils from '../utils/utils-metadata';
import withLanguageContext from '../../contexts/language/withLanguageContext';
import { PRODUCT_CLASS_LANGUAGE_LEARNING, PRODUCT_CLASS_LOTE } from '../../store/constants';
import { ProductListItemProps } from '../common/ProductList';
import { Narrator, ProductMetaData } from '../../../models/Product';
import { AddProduct } from '../common/AddProduct/AddProduct';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { Trans } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';
import { hasProductSelection } from '../product-selection/ProductSelectionUtils';

type Props = ProductListItemProps & {
    readonly closeSuggestions: () => void;
    readonly onMouseEnter: () => void;
    readonly language?: string;
    readonly onSelectPrice: () => Promise<void>;
};

const SearchCatalogueListItem: FC<Props> = ({
    product,
    onAddPrice = async () => {},
    productSelection,
    onProductImageClick,
    closeSuggestions,
    onSelectProduct,
    className,
    onMouseEnter,
    language,
    searchTerms = [],
    actionButtons,
    onSelectPrice,
    shopLanguage,
    isProductSelectable,
    ...props
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { value: ageCategory, searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    function onShowDetails() {
        setShowDetails(true);
    }

    function onHideDetails() {
        setShowDetails(false);
    }

    function getGenreLink(
        product: ProductMetaData,
        genre: {
            readonly id: string;
        },
        loanFormatKey: string,
        language: string
    ) {
        const isForeignLanguageProduct = product.productClass === PRODUCT_CLASS_LOTE;
        const isLanguageLearningProduct = product.productClass === PRODUCT_CLASS_LANGUAGE_LEARNING;

        if (isLanguageLearningProduct) {
            const filter = { genre: { include: [genre.id] } };
            return RoutingUtils.languageLearningDescriptorString({
                language,
                loanFormat: loanFormatKey,
                filter,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory: ageCategorySearchParam
            });
        }

        if (isForeignLanguageProduct && !isMultiLanguageShopEnabled) {
            return RoutingUtils.genrePageForeignLanguageLocationDescriptor({
                loanFormat: loanFormatKey,
                language,
                genreId: genre.id,
                ageCategory: ageCategorySearchParam
            });
        }

        return RoutingUtils.genrePageLocationDescriptor({
            loanFormat: loanFormatKey,
            genreId: genre.id,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory: ageCategorySearchParam
        });
    }

    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);

    //same value as loanFormatKey, loanFormatKey may change later
    const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);

    const packshotClasses = classNames('product-list-cell', 'packshot', { over: showDetails }, 'suggestions');

    let productLink = '#';
    if (!hasProductSelection(productSelection)) {
        productLink = `/products/${product.id}`;
    }

    const packshot = (
        <div className={packshotClasses}>
            <Link
                to={productLink}
                onClick={e => {
                    e.preventDefault();
                    onProductImageClick();
                    closeSuggestions();
                }}
            >
                <ProductCover
                    onMouseEnter={onShowDetails}
                    onMouseLeave={onHideDetails}
                    minSizeInPx={288}
                    product={product}
                />
            </Link>
            <SelectButton
                productOrId={product.id}
                productSelection={productSelection}
                onClick={onSelectProduct}
                isProductSelectable={isProductSelectable}
            />
            <PreviewButton product={product} />
        </div>
    );

    let seriesInner: ReactNode = null;

    if (product.seriesName) {
        let seriesLabel = product.seriesName;
        if (product.seriesNumber) {
            seriesLabel += ` - Book ${product.seriesNumber}`;
        }
        seriesInner = (
            <p onMouseEnter={onMouseEnter} className={className} key="series">
                <Link
                    to={MetadataUtils.createMetaDataLink({
                        productClass: product.productClass,
                        filter: {
                            series: { include: [product.seriesName] }
                        },
                        extra: { loanFormatKey, language: language },
                        shopLanguage,
                        isMultiLanguageShopEnabled,
                        ageCategory: ageCategorySearchParam
                    })}
                    onClick={closeSuggestions}
                >
                    <Highlighter
                        key={`cl-${product.seriesName}`}
                        autoEscape={true}
                        highlightClassName="suggestion-highlight"
                        searchWords={searchTerms as string[]}
                        textToHighlight={Utils.truncate(seriesLabel)}
                    />
                </Link>
            </p>
        );
    }

    const titleLabel = product.title + (product.subTitle ? ` ${product.subTitle}` : '');
    const combinedTitle = (
        <p title={titleLabel} className="label" key="title">
            <Link to={productLink} onClick={closeSuggestions}>
                <Highlighter
                    key={`cl-${product.subTitle}`}
                    autoEscape={true}
                    highlightClassName="suggestion-highlight"
                    searchWords={searchTerms as string[]}
                    textToHighlight={Utils.truncate(titleLabel)}
                />
            </Link>
        </p>
    );
    let authorsInner: ReactNode = null;
    if (product.authors) {
        const authors = Utils.filterUntilTextLength(product.authors, {
            getTextFunction: author => `${MetadataUtils.getPerformerFullname(author)}, `
        });
        const allShown = authors.length === product.authors.length;

        let authorComponents = authors.map((author, i) => {
            return (
                <Link
                    key={`${author.id}-${i}`}
                    to={MetadataUtils.createMetaDataLink({
                        productClass: product.productClass,
                        filter: {
                            author: { include: [author.id] }
                        },
                        extra: { loanFormatKey, language },
                        shopLanguage,
                        isMultiLanguageShopEnabled,
                        ageCategory: ageCategorySearchParam
                    })}
                    onClick={closeSuggestions}
                >
                    <Highlighter
                        key={`cl-${author.id}`}
                        autoEscape={true}
                        highlightClassName="suggestion-highlight"
                        searchWords={searchTerms as string[]}
                        textToHighlight={MetadataUtils.getPerformerFullname(author)!}
                    />
                </Link>
            );
        });

        authorComponents = Utils.joinComponents(authorComponents, <span key={`author_${product.id}`}>, </span>);
        authorsInner = (
            <p onMouseEnter={onMouseEnter} key="authors">
                {authorComponents}
                {!allShown ? ',...' : null}
            </p>
        );
    }

    let narratorsInner: ReactNode = null;
    if (product.narrators && product.narrators.length > 0) {
        const narrators = Utils.filterUntilTextLength(product.narrators, {
            getTextFunction: narrator => `${MetadataUtils.getPerformerFullname(narrator)}, `
        });

        const allShown = narrators.length === product.narrators.length;

        let narratorComponents = narrators.map((narrator: Narrator) => {
            return (
                <Link
                    key={narrator.id}
                    to={Utils.catalogueLocationDescriptorString({
                        loanFormat: mapLoanFormat(product.loanFormat),
                        filter: {
                            narrator: { include: [narrator.id] }
                        },
                        shopLanguage,
                        isMultiLanguageShopEnabled,
                        ageCategory: ageCategorySearchParam
                    })}
                    onClick={closeSuggestions}
                >
                    {MetadataUtils.getPerformerFullname(narrator)}
                </Link>
            );
        });

        narratorComponents = Utils.joinComponents(narratorComponents, <span key={`narrator_${product.id}`}>, </span>);
        const Narrators = () => <>{narratorComponents}</>;
        narratorsInner = (
            <p>
                <Trans i18nKey="components.searchResults.listItem.readBy">
                    <Narrators />
                </Trans>
                {!allShown ? ',...' : null}
            </p>
        );
    }

    const title = (
        <div className="tc tcw-25">
            {seriesInner}
            {combinedTitle}
            {authorsInner}
            {narratorsInner}
        </div>
    );

    let genreInner: ReactNode = null;

    if (product.genres) {
        const genres = product.genres.map(genre => {
            return (
                <Link
                    key={genre.id}
                    to={getGenreLink(product, genre, loanFormatKey, product.language!)}
                    onClick={closeSuggestions}
                >
                    <Highlighter
                        highlightClassName="suggestion-highlight"
                        searchWords={searchTerms as string[]}
                        textToHighlight={genre.name}
                    />
                </Link>
            );
        });

        genreInner = <p>{Utils.joinComponents(genres, ', ')}</p>;
    }

    //TODO: Remove feature toggle and add the Product Language Component for real
    const productLanguage = (
        <ProductLanguage
            product={product}
            label={product.language!}
            loanFormatKey={loanFormatKey}
            shopLanguage={shopLanguage}
        />
    );

    const info = (
        <div className="tc tcw-20">
            <ProductFormat
                product={product}
                {...props}
                loanFormatKey={productLoanFormatKey}
                shopLanguage={shopLanguage}
            />
            {genreInner}
            <ProductAge {...props} loanFormatKey={loanFormatKey} shopLanguage={shopLanguage} />
            {productLanguage}
        </div>
    );

    let publisherInner: ReactNode = null;
    if (product.publisher) {
        const publisherLink = (
            <Link
                to={MetadataUtils.createMetaDataLink({
                    productClass: product.productClass,
                    filter: {
                        publisher: { include: [product.publisher] }
                    },
                    extra: { loanFormatKey, language },
                    shopLanguage,
                    isMultiLanguageShopEnabled,
                    ageCategory: ageCategorySearchParam
                })}
                onClick={closeSuggestions}
            >
                <Highlighter
                    key={`cl-${product.publisher}`}
                    autoEscape={true}
                    highlightClassName="suggestion-highlight"
                    searchWords={searchTerms as string[]}
                    textToHighlight={product.publisher}
                />
            </Link>
        );

        let publisherImprintLink: ReactNode = null;
        if (product.publisherImprint) {
            publisherImprintLink = (
                <Link
                    to={MetadataUtils.createMetaDataLink({
                        productClass: product.productClass,
                        filter: {
                            publisherImprint: { include: [product.publisherImprint] }
                        },
                        extra: { loanFormatKey, language },
                        shopLanguage,
                        isMultiLanguageShopEnabled,
                        ageCategory: ageCategorySearchParam
                    })}
                    onClick={closeSuggestions}
                >
                    <Highlighter
                        key={`cl-${product.publisherImprint}`}
                        autoEscape={true}
                        highlightClassName="suggestion-highlight"
                        searchWords={searchTerms as string[]}
                        textToHighlight={product.publisherImprint}
                    />
                </Link>
            );
        }

        publisherInner = (
            <p>
                <span className="publisher-name">{publisherLink}</span>
                {publisherImprintLink ? <span className="hyphen"> - </span> : ''}
                <br />
                <span className="publisher-imprint">{publisherImprintLink}</span>
            </p>
        );
    }
    const isbn = (
        <div className="tc tcw-25">
            <Highlighter
                key={`cl-${product.isbn13}`}
                autoEscape={true}
                highlightClassName="suggestion-highlight"
                searchWords={searchTerms as string[]}
                textToHighlight={product.isbn13!}
            />
            <ProductReleaseDate product={product} {...props} />
            {publisherInner}
            <ProductUsageConditions
                product={product}
                loanFormatKey={loanFormatKey}
                shopLanguage={shopLanguage}
                ageCategory={ageCategory}
            />
        </div>
    );

    const actions = (
        <div className="tc tcw-20 actions mr-20">
            {actionButtons || null}
            <AddProduct
                product={product}
                onAddPrice={onAddPrice}
                onSelectPrice={onSelectPrice}
                showSelectedPriceOnly={false}
                allowSelection={true}
            />
        </div>
    );

    return (
        <div key={product.id} className="product-list-row suggestion-arrow-nav">
            {packshot}
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        {title}
                        {info}
                        {isbn}
                        {actions}
                    </div>
                </div>
                <div className="product-list-bottom">
                    <ProductStatistics
                        product={product}
                        additionalClassNames="fl-left"
                        loanFormatKey={productLoanFormatKey}
                    />
                    <CenteredListBadges>
                        <ListBadges
                            product={product}
                            loanFormatKey={loanFormatKey}
                            showAll={showDetails}
                            shopLanguage={shopLanguage}
                            closeSuggestions={closeSuggestions}
                        />
                    </CenteredListBadges>
                </div>
            </div>
        </div>
    );
};

export default withLanguageContext(SearchCatalogueListItem);
